import React, { useState, useEffect } from 'react';
import { FiSearch, FiFilter, FiX } from 'react-icons/fi';
import { countriesAPI } from '../../services/api';
import { Country, PaginatedResponse } from '../../types';
import CountryCard from './CountryCard';
import CountryCardSkeleton from './CountryCardSkeleton';
import SEO from '../SEO/SEO';

interface FilterState {
  visa: boolean | null;
  eta: boolean | null;
  preArrival: boolean | null;
  governmentPortal: boolean | null;
  mobileApps: boolean | null;
}

const CountriesList: React.FC = () => {
  const [allCountries, setAllCountries] = useState<Country[]>([]);
  const [filteredCountries, setFilteredCountries] = useState<Country[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<FilterState>({
    visa: null,
    eta: null,
    preArrival: null,
    governmentPortal: null,
    mobileApps: null,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20; // Items per page

  // Fetch paginated countries with proper cleanup
  useEffect(() => {
    const controller = new AbortController();
    let isMounted = true;

    const fetchCountries = async () => {
      try {
        setLoading(true);
        setError('');
        
        // First, fetch page 1 to get total pages
        const firstResponse = await countriesAPI.getCountries(1, pageSize, {
          signal: controller.signal
        });
        
        if (!isMounted) return;
        
        const firstData: PaginatedResponse<Country> = firstResponse.data;
        
        // Fetch ALL pages
        const pagesToFetch = firstData.total_pages;
        const allCountriesData: Country[] = [...firstData.data];
        
        // If there are more pages, fetch them in parallel
        if (pagesToFetch > 1) {
          const pagePromises = [];
          for (let page = 2; page <= pagesToFetch; page++) {
            pagePromises.push(
              countriesAPI.getCountries(page, pageSize, { signal: controller.signal })
            );
          }
          
          const responses = await Promise.all(pagePromises);
          
          if (!isMounted) return;
          
          responses.forEach((response) => {
            const data: PaginatedResponse<Country> = response.data;
            allCountriesData.push(...data.data);
          });
        }
        
        if (isMounted) {
          setAllCountries(allCountriesData);
          setFilteredCountries(allCountriesData);
        }
      } catch (err) {
        // Don't set error if request was cancelled
        if (err instanceof Error && err.name === 'AbortError') {
          return;
        }
        if (err instanceof Error && err.message.includes('canceled')) {
          return;
        }
        
        console.error('Countries fetch error:', err);
        if (isMounted) {
          const errorMessage = err instanceof Error ? err.message : 'Failed to fetch countries';
          setError(errorMessage);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchCountries();
    
    // Cleanup: abort request and mark as unmounted
    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [pageSize]);

  useEffect(() => {
    let filtered = allCountries.filter(country =>
      country.country.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (filters.visa !== null) {
      filtered = filtered.filter(country => filters.visa ? !!country.visa : !country.visa);
    }
    if (filters.eta !== null) {
      filtered = filtered.filter(country => filters.eta ? !!country.eta : !country.eta);
    }
    if (filters.preArrival !== null) {
      filtered = filtered.filter(country => filters.preArrival ? !!country.pre_arrival : !country.pre_arrival);
    }
    if (filters.governmentPortal !== null) {
      filtered = filtered.filter(country => filters.governmentPortal ? !!country.government_portal : !country.government_portal);
    }
    if (filters.mobileApps !== null) {
      filtered = filtered.filter(country => filters.mobileApps ? !!(country.mobile_apps && country.mobile_apps.length > 0) : !(country.mobile_apps && country.mobile_apps.length > 0));
    }

    // Reset to first page when filters or search change
    setCurrentPage(1);
    setFilteredCountries(filtered);
  }, [searchTerm, allCountries, filters]);

  const handleFilterChange = (filterType: keyof FilterState, value: boolean | null) => {
    setFilters(prev => ({ ...prev, [filterType]: value }));
  };

  const clearFilters = () => {
    setFilters({
      visa: null,
      eta: null,
      preArrival: null,
      governmentPortal: null,
      mobileApps: null,
    });
  };

  const getActiveFiltersCount = () => {
    return Object.values(filters).filter(value => value !== null).length;
  };

  // Calculate pagination for filtered results
  const totalFilteredPages = Math.ceil(filteredCountries.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const displayedCountries = filteredCountries.slice(startIndex, endIndex);
  
  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalFilteredPages) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
            <div className="h-10 w-80 mx-auto mb-4 rounded-lg animate-pulse bg-slate-200 dark:bg-slate-700"></div>
            <div className="h-6 w-96 mx-auto rounded-lg animate-pulse bg-slate-200 dark:bg-slate-700"></div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(9)].map((_, index) => (
            <CountryCardSkeleton key={index} />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto">
      <div className="p-8 rounded-2xl border bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800/50">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-red-100 dark:bg-red-900/40">
              <span className="text-2xl text-red-600 dark:text-red-300">!</span>
            </div>
            <div>
              <h3 className="text-xl font-bold mb-2 text-red-800 dark:text-red-200">
                Error Loading Data
              </h3>
              <p className="text-red-700 dark:text-red-300">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Countries Travel Information - travelbaba786',
    url: 'https://travelbaba786.com/countries',
    description: 'Find visa and travel requirements for countries worldwide. Search and filter countries by visa requirements, ETA, pre-arrival forms, and government portals.',
    mainEntity: {
      '@type': 'ItemList',
      name: 'Countries with Travel Information',
      description: 'Comprehensive directory of countries with visa requirements, ETA information, and official government portals',
      numberOfItems: allCountries.length > 0 ? allCountries.length : '200+',
    },
  };

  return (
    <>
      <SEO
        title="Countries Travel Information - Find Visa Requirements Worldwide"
        description="Find visa and travel requirements for countries worldwide. Search and filter countries by visa requirements, ETA, pre-arrival forms, and government portals. Access verified official government websites."
        keywords="countries visa, visa requirements, country visa information, ETA by country, travel requirements by country, visa search, country travel guide"
        url="https://travelbaba786.com/countries"
        structuredData={structuredData}
      />
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
      <div className="mb-10">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-4xl md:text-5xl font-bold mb-3 text-gray-900 dark:text-white">
              Travel Information
            </h1>
            <p className="text-lg text-gray-600 dark:text-gray-400">
              Find visa and travel requirements for countries worldwide
            </p>
          </div>
        </div>

        {/* Search and Filter Bar */}
        <div className="flex gap-4">
          <div className="flex-1 relative">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <FiSearch className="w-5 h-5 text-gray-400 dark:text-gray-500" />
            </div>
            <input
              type="text"
              placeholder="Search countries..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 rounded-xl border-2 transition-all duration-300 bg-white dark:bg-slate-800 border-gray-300 dark:border-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:border-blue-500"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute inset-y-0 right-0 pr-4 flex items-center"
              >
                <FiX className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              </button>
            )}
          </div>

          <button
            onClick={() => setShowFilters(!showFilters)}
            className="px-6 py-3 rounded-xl border-2 transition-all duration-300 flex items-center gap-2 bg-white dark:bg-slate-800 border-gray-300 dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-600 text-gray-700 dark:text-white"
          >
            <FiFilter className="w-5 h-5" />
            <span className="font-semibold">Filters</span>
            {getActiveFiltersCount() > 0 && (
              <span className="px-2 py-0.5 rounded-lg text-xs font-bold bg-blue-600 text-white">
                {getActiveFiltersCount()}
              </span>
            )}
          </button>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <div className="mt-4 p-6 rounded-xl border transition-all duration-300 bg-white dark:bg-slate-800 border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                Filter Requirements
              </h3>
              <div className="flex items-center gap-3">
                {getActiveFiltersCount() > 0 && (
                  <button
                    onClick={clearFilters}
                    className="text-sm font-semibold text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                  >
                    Clear All
                  </button>
                )}
                <FiX
                  onClick={() => setShowFilters(false)}
                  className="w-5 h-5 cursor-pointer text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4">
              {[
                { key: 'visa' as keyof FilterState, label: 'Visa Required' },
                { key: 'eta' as keyof FilterState, label: 'ETA Required' },
                { key: 'preArrival' as keyof FilterState, label: 'Pre-Arrival Form' },
                { key: 'governmentPortal' as keyof FilterState, label: 'Gov Portal' },
                { key: 'mobileApps' as keyof FilterState, label: 'Mobile Apps' },
              ].map(({ key, label }) => (
                <div key={key} className="flex flex-col gap-2">
                  <label className="text-sm font-semibold text-gray-600 dark:text-gray-400">
                    {label}
                  </label>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleFilterChange(key, filters[key] === true ? null : true)}
                      className={`flex-1 px-3 py-2 rounded-lg text-sm font-semibold transition-all ${
                        filters[key] === true
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-slate-600'
                      }`}
                    >
                      Yes
                    </button>
                    <button
                      onClick={() => handleFilterChange(key, filters[key] === false ? null : false)}
                      className={`flex-1 px-3 py-2 rounded-lg text-sm font-semibold transition-all ${
                        filters[key] === false
                          ? 'bg-gray-600 text-white'
                          : 'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-slate-600'
                      }`}
                    >
                      No
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Results Count */}
      <div className="flex items-center justify-between mb-8">
        <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            {filteredCountries.length} {filteredCountries.length === 1 ? 'Country' : 'Countries'} Found
          </h2>
          {(searchTerm || getActiveFiltersCount() > 0) && (
            <p className="text-sm mt-1 text-gray-600 dark:text-gray-400">
              {searchTerm && `Searching: "${searchTerm}" • `}
              {getActiveFiltersCount() > 0 && `${getActiveFiltersCount()} filter${getActiveFiltersCount() > 1 ? 's' : ''} active`}
            </p>
          )}
        </div>
      </div>

      {/* Countries Grid */}
      {displayedCountries.length > 0 ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {displayedCountries.map((country) => (
              <CountryCard key={country.country} country={country} />
            ))}
          </div>

          {/* Pagination Controls */}
          {totalFilteredPages > 1 && (
            <div className="mt-12 flex items-center justify-center gap-2">
              {/* Previous Button */}
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl font-semibold transition-all ${
                  currentPage === 1
                    ? 'bg-gray-100 dark:bg-slate-700 text-gray-400 dark:text-gray-600 cursor-not-allowed'
                    : 'bg-white dark:bg-slate-800 border-2 border-gray-300 dark:border-gray-700 text-gray-700 dark:text-white hover:border-gray-400 dark:hover:border-gray-600'
                }`}
              >
                <span className="text-xl">‹</span>
                <span className="hidden sm:inline">Previous</span>
              </button>

              {/* Page Numbers */}
              <div className="flex items-center gap-1">
                {/* Show 5 pages on desktop */}
                <div className="hidden sm:flex items-center gap-1">
                  {Array.from({ length: Math.min(totalFilteredPages, 5) }, (_, i) => {
                    let pageNum: number;
                    if (totalFilteredPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalFilteredPages - 2) {
                      pageNum = totalFilteredPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`min-w-[40px] h-10 rounded-xl font-semibold transition-all ${
                          pageNum === currentPage
                            ? 'bg-blue-600 text-white shadow-lg'
                            : 'bg-white dark:bg-slate-800 border-2 border-gray-300 dark:border-gray-700 text-gray-700 dark:text-white hover:border-gray-400 dark:hover:border-gray-600'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                
                {/* Mobile: Show only current page */}
                <div className="sm:hidden flex items-center gap-1">
                  <button className="min-w-[32px] h-8 rounded-lg text-sm font-semibold bg-blue-600 text-white">
                    {currentPage}
                  </button>
                </div>
              </div>

              {/* Next Button */}
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalFilteredPages}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl font-semibold transition-all ${
                  currentPage === totalFilteredPages
                    ? 'bg-gray-100 dark:bg-slate-700 text-gray-400 dark:text-gray-600 cursor-not-allowed'
                    : 'bg-white dark:bg-slate-800 border-2 border-gray-300 dark:border-gray-700 text-gray-700 dark:text-white hover:border-gray-400 dark:hover:border-gray-600'
                }`}
              >
                <span className="hidden sm:inline">Next</span>
                <span className="text-xl">›</span>
              </button>
            </div>
          )}

          {/* Pagination Info */}
          {filteredCountries.length > 0 && (
            <div className="mt-6 text-center text-sm text-gray-600 dark:text-gray-400">
              Showing {startIndex + 1}-{Math.min(endIndex, filteredCountries.length)} of {filteredCountries.length}
              {searchTerm && <span className="hidden sm:inline"> matching "{searchTerm}"</span>}
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-16">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 bg-gray-100 dark:bg-slate-800">
            <FiSearch className="w-8 h-8 text-gray-400 dark:text-gray-600" />
          </div>
          <h3 className="text-2xl font-bold mb-2 text-gray-900 dark:text-white">
            No countries found
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {searchTerm 
              ? `No results match "${searchTerm}"`
              : 'Try adjusting your filters'
            }
          </p>
          <button
            onClick={() => {
              setSearchTerm('');
              clearFilters();
            }}
            className="px-6 py-3 rounded-xl font-semibold transition-all bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-gray-300 dark:hover:bg-gray-600"
          >
            Clear All
          </button>
        </div>
      )}
    </div>
    </>
  );
};

export default CountriesList;
